import React, { useState, useEffect, useRef, useCallback } from 'react';
import { colorwayFor } from '../colorways';
import { IconCheck, IconAlert, IconX, IconStamp, IconTag } from '../icons';

const QUICK = [1, 2, 3, 5, 10];

const TABS = [
  { id: 'add', label: 'Add' },
  { id: 'spend', label: 'Spend' },
  { id: 'history', label: 'History' },
];

export default function ChildModal({ child, spendingChoices, onClose, onAddTokens, onSpendTokens, API }) {
  const dialogRef = useRef(null);
  const [tab, setTab] = useState('add');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [currentTokens, setCurrentTokens] = useState(child.tokens);
  const [landKey, setLandKey] = useState(0);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [busy, setBusy] = useState(false);

  const { primary, secondary } = colorwayFor(child.id);

  // <dialog> gives us the focus trap, Escape, the inert background and focus
  // return to the box that opened it. None of that needs writing.
  useEffect(() => {
    const el = dialogRef.current;
    if (el && !el.open) el.showModal();
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API}/children/${child.id}/history`);
      setHistory(await res.json());
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [API, child.id]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  const flash = (updated, text) => {
    setCurrentTokens(updated.tokens);
    setLandKey(k => k + 1);
    setMessage(text);
    setTimeout(() => setMessage(null), 4000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const n = parseInt(amount, 10);
    if (!n || n <= 0) return;
    setError(null);
    setBusy(true);
    try {
      const updated = await onAddTokens(child.id, n, description.trim() || 'Token award');
      flash(updated, `Added ${n} ${n === 1 ? 'token' : 'tokens'} to ${child.name}.`);
      setAmount('');
      setDescription('');
      if (tab === 'history') loadHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSpend = async () => {
    if (!selectedChoice) return;
    setError(null);
    setBusy(true);
    try {
      const updated = await onSpendTokens(child.id, selectedChoice.id);
      flash(updated, `${child.name} spent ${selectedChoice.price} on ${selectedChoice.name}.`);
      setSelectedChoice(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const switchTab = (id) => {
    setTab(id);
    setMessage(null);
    setError(null);
  };

  const formatDate = (s) => {
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
    return isNaN(d) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const parsed = parseInt(amount, 10);
  const canAdd = Number.isInteger(parsed) && parsed > 0;

  return (
    <dialog
      ref={dialogRef}
      className="dialog"
      aria-labelledby="box-title"
      onClose={onClose}
      onClick={(e) => { if (e.target === dialogRef.current) onClose(); }}
    >
      <div className="dialog-lid">
        <div className="dialog-grip" aria-hidden="true"><span /></div>

        <div className="dialog-head">
          <div className="dialog-title">
            <h2 id="box-title">{child.name}</h2>
            <p>{primary.name} / {secondary.name}</p>
          </div>
          <p className="dialog-figure">
            <span key={landKey} className="fig fig--landed">{currentTokens}</span>
            <span className="unit" aria-hidden="true">
              {currentTokens === 1 ? 'Token' : 'Tokens'}
            </span>
            <span className="sr-only">
              {currentTokens === 1 ? '1 token' : `${currentTokens} tokens`}
            </span>
          </p>
          <button type="button" className="btn-icon" onClick={onClose} aria-label={`Close ${child.name}'s box`}>
            <IconX />
          </button>
        </div>

        <div className="tabs" role="tablist" aria-label={`${child.name}'s box`}>
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`tab-${t.id}`}
              className="tab"
              aria-selected={tab === t.id}
              aria-controls={`panel-${t.id}`}
              tabIndex={tab === t.id ? 0 : -1}
              onClick={() => switchTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div aria-live="polite" role="status">
          {message && (
            <p className="note note--good"><IconCheck />{message}</p>
          )}
        </div>
        <div aria-live="assertive" role="alert">
          {error && (
            <p className="note note--bad"><IconAlert />{error}</p>
          )}
        </div>

        {tab === 'add' && (
          <form className="stack" id="panel-add" role="tabpanel" aria-labelledby="tab-add" onSubmit={handleAdd}>
            <div className="field">
              <label htmlFor="amount">How many tokens?</label>
              <input
                id="amount"
                className="input input--num"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0"
              />
              <div className="chips">
                {QUICK.map(n => (
                  <button
                    key={n}
                    type="button"
                    className="chip"
                    aria-pressed={parsed === n}
                    onClick={() => setAmount(String(n))}
                  >
                    +{n}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label htmlFor="reason">What for? (optional)</label>
              <input
                id="reason"
                className="input"
                type="text"
                maxLength={80}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Tidied the whole room"
              />
            </div>

            <button type="submit" className="btn btn--primary btn--block" disabled={!canAdd || busy}>
              <IconStamp />
              {busy ? 'Stamping…' : canAdd ? `Add ${parsed} to ${child.name}` : 'Add tokens'}
            </button>
          </form>
        )}

        {tab === 'spend' && (
          <div className="stack" id="panel-spend" role="tabpanel" aria-labelledby="tab-spend">
            {spendingChoices.length === 0 ? (
              <div className="empty">
                <h2>Nothing on the shelf</h2>
                <p>A grown-up needs to put some rewards up before tokens can be spent.</p>
              </div>
            ) : (
              <>
                <div className="picks">
                  {spendingChoices.map(choice => {
                    const short = choice.price - currentTokens;
                    const affordable = short <= 0;
                    const chosen = selectedChoice?.id === choice.id;
                    return (
                      <button
                        key={choice.id}
                        type="button"
                        className="pick"
                        aria-pressed={affordable ? chosen : undefined}
                        // aria-disabled, not disabled: a child saving up must still
                        // be able to reach this row and hear how far off they are.
                        aria-disabled={!affordable || busy}
                        onClick={() => {
                          if (!affordable || busy) return;
                          setSelectedChoice(chosen ? null : choice);
                        }}
                      >
                        <span className="pick-info">
                          <span className="pick-name">{choice.name}</span>
                          {choice.description && (
                            <span className="pick-desc">{choice.description}</span>
                          )}
                          {!affordable && (
                            <span className="pick-short">
                              {short} more {short === 1 ? 'token' : 'tokens'} to go
                            </span>
                          )}
                        </span>
                        <span className="pick-price">{choice.price}</span>
                      </button>
                    );
                  })}
                </div>

                {selectedChoice && (
                  <button type="button" className="btn btn--primary btn--block" onClick={handleSpend} disabled={busy}>
                    <IconTag />
                    {busy ? 'Ringing up…' : `Spend ${selectedChoice.price} on ${selectedChoice.name}`}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div id="panel-history" role="tabpanel" aria-labelledby="tab-history">
            {loadingHistory && <p className="box-desc">Looking&hellip;</p>}
            {!loadingHistory && history.length === 0 && (
              <div className="empty">
                <h2>Nothing yet</h2>
                <p>Nothing has gone in or out of this box so far.</p>
              </div>
            )}
            {history.length > 0 && (
              <div className="slip">
                {history.map(item => (
                  <div key={item.id} className="slip-row">
                    <span className="slip-what">{item.description}</span>
                    <span className={`slip-amt slip-amt--${item.type}`}>
                      {item.type === 'add' ? '+' : '−'}{item.amount}
                      <span className="sr-only">
                        {item.type === 'add' ? ' tokens in' : ' tokens out'}
                      </span>
                    </span>
                    <span className="slip-when">{formatDate(item.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </dialog>
  );
}
